import { useState, useEffect, useCallback, useMemo } from "react";
import { usePagination } from "../../../../hooks/usePagination";
import { useTenant } from "../../../../hooks/useTenant";
import { toast } from "react-toastify";
import AcquirerService from "../../../../services/acquirer_service";
import BankAccountService from "../../../../services/bank_account_service";

/**
 * useAcquirers
 * Hook de lógica para o módulo de Adquirentes.
 * Responsabilidade: estados de lista, CRUD via Service, loading e toasts.
 * NÃO contém interações de UI (modais, dialogs).
 */
export const useAcquirers = () => {
    const { idTenant, user } = useTenant();
    const [isSaving, setIsSaving] = useState(false);
    const [isInitialLoad, setIsInitialLoad] = useState(true);
    const [bankAccounts, setBankAccounts] = useState([]);

    const paginationOptions = useMemo(() => ({
        pageSize: 50, orderField: "name", orderDirection: "asc"
    }), []);

    // Paginação das Adquirentes
    const { data: acquirers, loading, error, refresh } = usePagination(
        `tenants/${idTenant}/acquirers`,
        idTenant,
        paginationOptions
    );

    // Seta isInitialLoad como false após a primeira carga
    useEffect(() => {
        if (!loading && isInitialLoad) {
            setIsInitialLoad(false);
        }
    }, [loading, isInitialLoad]);

    useEffect(() => {
        if (!idTenant) return;
        const fetchAccounts = async () => {
            try {
                const accounts = await BankAccountService.getBankAccounts(idTenant);
                setBankAccounts(accounts);
            } catch (error) {
                console.error("Erro ao buscar contas bancárias:", error);
            }
        };
        fetchAccounts();
    }, [idTenant]);

    /**
     * Salvar uma Adquirente
     */
    const saveAcquirer = useCallback(async (formData) => {
        if (!idTenant) return;
        setIsSaving(true);

        try {
            await AcquirerService.save(formData, idTenant, user, acquirers);
            toast.success(formData.id ? "Adquirente atualizada!" : "Nova adquirente cadastrada!");
            refresh();
            return true;
        } catch (err) {
            console.error("Erro ao salvar adquirente:", err);
            toast.error("Erro ao salvar adquirente.");
            return false;
        } finally {
            setIsSaving(false);
        }
    }, [idTenant, user, acquirers, refresh]);

    /**
     * Excluir uma Adquirente (sem ConfirmDialog — a confirmação fica no index.js)
     */
    const deleteAcquirer = useCallback(async (acquirerId) => {
        if (!idTenant) return;

        const acquirerToDelete = acquirers.find(a => a.id === acquirerId);

        try {
            await AcquirerService.delete(acquirerId, idTenant, user, acquirerToDelete?.name);
            toast.success("Adquirente removida!");
            refresh();
            return true;
        } catch (err) {
            console.error("Erro ao excluir adquirente:", err);
            toast.error("Não foi possível excluir a adquirente.");
            return false;
        }
    }, [idTenant, user, acquirers, refresh]);

    return {
        acquirers,
        bankAccounts,
        loading,
        isInitialLoad,
        error,
        isSaving,
        actions: {
            saveAcquirer,
            deleteAcquirer,
            refresh
        }
    };
};
